/**
 * deepspace dev [--prod] [--port N]
 *
 * Starts local development:
 *   1. Ensures you're logged in
 *   2. Writes .dev.vars pointing to dev or prod workers
 *   3. Starts vite dev (Cloudflare Vite plugin runs the worker in-process)
 *
 *   deepspace dev                   # uses dev workers, port 5173 (strict)
 *   deepspace dev --prod            # uses production workers
 *   deepspace dev --port 5180       # bind to a different port (multi-app dev)
 *
 * Port is `--port` > $DEEPSPACE_PORT > 5173. We always pass --strictPort to
 * vite so a busy port fails loudly instead of silently jumping to 5174 (which
 * would diverge from anything Playwright/test config is expecting).
 */

import { defineCommand } from 'citty'
import { resolve, basename } from 'node:path'
import spawn from 'cross-spawn'
import { ensureToken } from '../auth'
import { detectAppName, findAppDir, findChildApps, writeLaunchConfigIfMissing } from '../lib/app-context'
import { writeDevVars } from '../env'
import { decodeJwtPayload } from '../jwt'
import { ensureInstallReady } from '../lib/install-status'
import { preflightWindowsWorkerd } from '../lib/preflight'

const DEFAULT_PORT = 5173

export default defineCommand({
  meta: {
    name: 'dev',
    description: 'Start local development server',
  },
  args: {
    dir: {
      type: 'positional',
      description: 'App directory (default: current directory)',
      required: false,
    },
    prod: {
      type: 'boolean',
      description: 'Use production workers (real auth, real API calls)',
      default: false,
    },
    env: {
      type: 'string',
      description:
        'wrangler.toml [env.<name>] block to run (e.g. --env staging). ' +
        'Reads .dev.vars.<name> and applies the env\'s overrides at build time.',
      required: false,
    },
    port: {
      type: 'string',
      description: `Port to bind (default ${DEFAULT_PORT}, or $DEEPSPACE_PORT)`,
      required: false,
    },
  },
  async run({ args }) {
    const env = args.prod ? 'prod' : 'dev'
    const wranglerEnv = typeof args.env === 'string' && args.env.trim() ? args.env.trim() : undefined
    const port = resolvePort(args.port)

    // Resolve the app root by walking up from the requested dir (default cwd),
    // so running from a subdirectory still works instead of hard-failing.
    const start = resolve(args.dir ?? '.')
    const appDir = findAppDir(start)
    if (!appDir) {
      console.error(`No wrangler.toml found at or above ${start}.`)
      const children = findChildApps(start)
      if (children.length > 0) {
        console.error('Did you mean to run inside one of these app directories?')
        for (const c of children) console.error(`  cd ${c}`)
      } else {
        console.error('Run from a DeepSpace app directory (one containing wrangler.toml).')
      }
      process.exit(1)
    }

    // Seed an app-local .claude/launch.json (only if absent) so the Claude Code
    // preview tool launches this app on the resolved port, instead of walking
    // up and latching onto an ancestor repo's config.
    writeLaunchConfigIfMissing(appDir, detectAppName(appDir) ?? basename(appDir), port)

    ensureInstallReady(appDir)

    let token: string
    try {
      token = await ensureToken()
    } catch (err: any) {
      console.error(err.message)
      process.exit(1)
    }

    let payload: { sub: string; name?: string; email?: string }
    try {
      payload = decodeJwtPayload<{ sub: string; name?: string; email?: string }>(token)
    } catch {
      console.error('Malformed session token. Run `npx deepspace login`.')
      process.exit(1)
    }
    // Two distinct concepts both colloquially called "environment":
    //   - Platform : which DeepSpace stack we point at (dev vs prod
    //                auth-worker / api-worker / platform-worker URLs)
    //   - Wrangler env : which `[env.<name>]` block of wrangler.toml
    //                    we apply (renames the app, swaps vars, etc.)
    // Labeling them differently in the startup log makes the override
    // surface obvious when debugging which app slot a session is
    // hitting.
    console.log(`Logged in as ${payload.name ?? payload.email}`)
    console.log(`Platform: ${env}`)
    if (wranglerEnv) console.log(`Wrangler env: ${wranglerEnv}`)
    console.log(`Port: ${port}`)

    await writeDevVars(appDir, env, payload.sub, token, wranglerEnv)
    preflightWindowsWorkerd(appDir)
    console.log('Starting dev server...\n')

    const vite = spawn(
      'npx',
      ['vite', '--port', String(port), '--strictPort', '--host'],
      {
        cwd: appDir,
        stdio: 'inherit',
        // CLOUDFLARE_ENV is what the Cloudflare Vite plugin keys off — it
        // applies `[env.<name>]` overrides and loads `.dev.vars.<name>`
        // instead of `.dev.vars`. Set only when --env was passed so the
        // default invocation behaves exactly as before.
        env: {
          ...process.env,
          DEEPSPACE_PORT: String(port),
          ...(wranglerEnv ? { CLOUDFLARE_ENV: wranglerEnv } : {}),
        },
      },
    )
    process.on('SIGINT', () => vite.kill())
    process.on('SIGTERM', () => vite.kill())
    vite.on('close', (code) => process.exit(code ?? 0))
  },
})

function resolvePort(arg?: string): number {
  const raw = arg ?? process.env.DEEPSPACE_PORT
  if (!raw) return DEFAULT_PORT
  const n = Number(raw)
  if (!Number.isInteger(n) || n < 1 || n > 65535) {
    console.error(`Invalid port: ${raw}. Must be an integer between 1 and 65535.`)
    process.exit(1)
  }
  return n
}
